import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { HomeworkPage } from './components/HomeworkPage';
import { HomeworkProgressionPage } from './components/HomeworkProgressionPage';
import BehaviourPage from './components/BehaviourPage';
import JobsPage from './components/JobsPage';
import { InstallPrompt } from './components/InstallPrompt';
import { NotificationBell } from './components/NotificationBell';
import { UserMenu } from './components/UserMenu';
import { AuthGuard } from './components/AuthGuard';
import { useAuth } from './hooks/useAuth';
import { useGmailStatus } from './hooks/useGmailStatus';

const ADMIN_EMAIL = 'dave@activelylazy.co.uk';

function AppContent() {
  const { user, logout } = useAuth();
  const { isAuthenticated: gmailAuthenticated, isLoading: gmailLoading } = useGmailStatus();
  const location = useLocation();

  const isAdmin = user?.email === ADMIN_EMAIL;
  const showGmailAuthButton = isAdmin && !gmailLoading && !gmailAuthenticated;

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        {/* Top header bar */}
        <div className="border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex items-center gap-3">
                <img
                  src="/logo.png"
                  alt="Green Family"
                  className="h-10 w-10"
                />
                <h1 className="text-lg font-semibold text-gray-900">Green Family</h1>
              </div>
              {user && (
                <div className="flex items-center gap-3">
                  {showGmailAuthButton && (
                    <a
                      href="/api/auth/google/setup"
                      title="Gmail authentication required - click to authenticate"
                      className="p-2 text-orange-600 hover:text-orange-700 transition-colors relative"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="absolute -top-1 -right-1 h-3 w-3 bg-orange-600 rounded-full border-2 border-white"></span>
                    </a>
                  )}
                  <NotificationBell />
                  <UserMenu name={user.name} picture={user.picture} onLogout={logout} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab navigation */}
        {user && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex gap-8 border-b border-gray-200">
              <Link
                to="/"
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  location.pathname === '/' || location.pathname.startsWith('/homework')
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Homework
              </Link>
              <Link
                to="/behaviour"
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  location.pathname === '/behaviour'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Behaviour
              </Link>
              <Link
                to="/jobs"
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  location.pathname === '/jobs'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Jobs
              </Link>
            </div>
          </div>
        )}
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <Routes>
          <Route path="/" element={<HomeworkPage />} />
          <Route path="/homework/:assignmentId" element={<HomeworkProgressionPage />} />
          <Route path="/behaviour" element={<BehaviourPage />} />
          <Route path="/jobs" element={<JobsPage />} />
        </Routes>
      </main>

      <InstallPrompt />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthGuard>
        <AppContent />
      </AuthGuard>
    </BrowserRouter>
  );
}

export default App;
